import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import PaymentNotification from '../../components/PaymentNotification';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

const PaymentSessions = () => {
  const { userProfile, session } = useAuth();
  const { t } = useLanguage();
  const [activeSessions, setActiveSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const deletedSessionIdsRef = useRef(new Set()); // Track deleted sessions using ref
  const [loading, setLoading] = useState(true);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [showCreateQRSession, setShowCreateQRSession] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newSession, setNewSession] = useState({
    device_id: '',
    product_id: '',
    offer_id: '', // Add offer_id support
    quantity: 1,
    final_amount: ''
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [productOffers, setProductOffers] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [products, setProducts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const [qrPaymentUrl, setQrPaymentUrl] = useState(null);
  const [eventSource, setEventSource] = useState(null);
  
  // Pagination state for completed transactions
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Show 5 transactions per page

  const fetchSessions = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      // Get all sessions (both active and completed)
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/sale-sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Sessions response status:', response.status);
      const data = await response.json();
      console.log('Sessions response data:', data);

      if (response.ok && data.success) {
        // Filter active and completed sessions
        const active = data.data.filter(s => s.status === 'waiting_for_nfc' && new Date(s.session_expires_at) > new Date());
        const completed = data.data.filter(s => s.status === 'payment_completed');
        
        // Format session data inline - use current products state
        const formatSession = (session) => {
          // Access products from closure - will use current state when called
          const product = products.find(p => p.id === session.product_id);
          const productName = product ? product.product_name : t('unknownProduct');
          return {
            id: session.id,
            amount: session.final_amount || session.amount || 0,
            description: `${productName} (Qty: ${session.quantity || 1})`,
            status: session.status || 'waiting_for_nfc',
            paymentMethod: session.payment_method || session.paymentMethod || 'nfc',
            nfcTagId: session.nfc_tag_id || session.nfcTagId,
            qrCodeHash: session.qr_code_hash || session.qrCodeHash,
            createdAt: session.created_at || session.createdAt,
            expiresAt: session.session_expires_at || session.expiresAt,
            customerInfo: session.customer_info || session.customerInfo,
            deviceId: session.device_id || session.deviceId,
            productId: session.product_id || session.productId,
            quantity: session.quantity || 1,
            finalAmount: session.final_amount || session.finalAmount || session.amount || 0,
            discountApplied: session.discount_amount || session.discountApplied || 0,
            transactionId: session.transaction_id || session.transactionId,
            completedAt: session.payment_completed_at || session.completedAt,
            productName: productName
          };
        };
        
        // Filter out deleted sessions to prevent them from reappearing
        const activeFiltered = active
          .filter(s => !deletedSessionIdsRef.current.has(s.id))
          .map(formatSession);
        const completedFiltered = completed
          .filter(s => !deletedSessionIdsRef.current.has(s.id))
          .map(formatSession);
        
        setActiveSessions(activeFiltered);
        setCompletedSessions(completedFiltered);
      } else {
        setError(data.error || t('failedToFetchSessions'));
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError(t('failedToFetchSessions'));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.token, session?.access_token, t]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setProducts(data.data || []);
      } else {
        setError(data.error || t('failedToFetchProducts'));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(t('failedToFetchProducts'));
    } finally {
      setLoadingProducts(false);
    }
  }, [userProfile?.token, session?.access_token, t]);

  const fetchDevices = useCallback(async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setDevices(data.data || []);
      } else {
        setError(data.error || t('failedToFetchDevices'));
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError(t('failedToFetchDevices'));
    }
  }, [userProfile?.token, session?.access_token, t]);

  // Calculate total amount for selected products
  const calculateTotal = () => {
    return selectedProducts.reduce((total, productId) => {
      const product = products.find(p => p.id === productId);
      const quantity = productQuantities[productId] || 1;
      return total + (product ? product.original_price * quantity : 0);
    }, 0);
  };

  // Handle product selection
  const handleProductSelection = (productId, isSelected) => {
    if (isSelected) {
      setSelectedProducts(prev => [...prev, productId]);
      setProductQuantities(prev => ({ ...prev, [productId]: 1 }));
      setProductOffers(prev => ({ ...prev, [productId]: '' }));
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
      setProductQuantities(prev => {
        const newQuantities = { ...prev };
        delete newQuantities[productId];
        return newQuantities;
      });
      setProductOffers(prev => {
        const newOffers = { ...prev };
        delete newOffers[productId];
        return newOffers;
      });
    }
  };

  // Handle quantity change
  const handleQuantityChange = (productId, quantity) => {
    setProductQuantities(prev => ({ ...prev, [productId]: Math.max(1, quantity) }));
  };

  // Reset form when modal is closed
  const resetForm = () => {
    setSelectedProducts([]);
    setProductQuantities({});
    setProductOffers({});
    setNewSession({
      device_id: '',
      product_id: '',
      offer_id: '',
      quantity: 1,
      final_amount: ''
    });
  };

  // Initial load only - don't refetch automatically
  useEffect(() => {
    fetchSessions();
    fetchProducts();
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Server-Sent Events for real-time payment notifications
  // Only recreate connections when session IDs change, not on every render
  const activeSessionIds = useMemo(() => activeSessions.map(s => s.id).join(','), [activeSessions]);
  
  useEffect(() => {
    if (activeSessions.length === 0) {
      // Close existing connection if no active sessions
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
      return;
    }

    const token = userProfile?.token || session?.access_token;
    if (!token) return;

    // Close existing connection
    if (eventSource) {
      eventSource.close();
    }

    const eventSources = [];

    // Create new EventSource for each active session
    activeSessions.forEach(session => {
      const sessionEventSource = new EventSource(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/sale-sessions/${session.id}/notifications?token=${token}`
      );

      sessionEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔔 Payment notification received:', data);
          
          switch (data.type) {
            case 'payment_completed':
              console.log('✅ Payment completed for session:', session.id);
              setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'payment_success',
                session: session,
                amount: data.paymentDetails?.amount,
                timestamp: new Date().toISOString()
              }]);
              // Refresh sessions after payment completion instead of local update
              fetchSessions({ silent: true });
              break;
            case 'payment_failed':
              console.log('❌ Payment failed for session:', session.id);
              setError(`${t('paymentFailed')}: ${data.error}`);
              break;
            case 'session_expired':
              console.log('⏰ Session expired:', session.id);
              // Refresh sessions after expiration
              fetchSessions({ silent: true });
              break;
            default:
              console.log('Unknown notification type:', data.type);
              break;
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      sessionEventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
      };

      eventSources.push(sessionEventSource);
    });

    // Store the first event source for cleanup tracking
    if (eventSources.length > 0) {
      setEventSource(eventSources[0]);
    }

    // Cleanup on unmount or when session IDs change
    return () => {
      eventSources.forEach(es => es.close());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionIds, userProfile?.token, session?.access_token, t, fetchSessions]); // Only recreate when session IDs change

  const createSaleSession = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      // Validate that at least one product is selected
      if (selectedProducts.length === 0) {
        setError(t('pleaseSelectAtLeastOneProductError'));
        return;
      }

      const totalAmount = calculateTotal();
      if (totalAmount <= 0) {
        setError(t('totalAmountMustBeGreaterThanZero'));
        return;
      }

      const sessionData = {
        products: selectedProducts.map(productId => {
          const productData = {
            product_id: productId,
            quantity: productQuantities[productId] || 1
          };
          // Add offer_id if specified for this product
          if (productOffers[productId] && productOffers[productId].trim() !== '') {
            productData.offer_id = productOffers[productId];
          }
          return productData;
        }),
        final_amount: totalAmount,
        payment_method: 'nfc'
      };

      // Only include device_id if specified
      if (newSession.device_id) {
        sessionData.device_id = newSession.device_id;
      }

      console.log('Creating NFC session with data:', sessionData);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/sale-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      console.log('Create session response status:', response.status);
      const data = await response.json();
      console.log('Create session response data:', data);

      if (response.ok && data.success) {
        setShowCreateSession(false);
        resetForm();
        // Add the newly created session locally to active sessions
        if (data.data) {
          const formatted = formatSessionData(data.data);
          setActiveSessions(prev => [formatted, ...prev]);
        }
      } else {
        setError(data.error || t('failedToCreateSession'));
      }
    } catch (error) {
      console.error('Error creating session:', error);
      if (error.name === 'AbortError') {
        setError(t('requestTimedOut'));
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError(t('networkErrorCheckServer'));
      } else {
        setError(`${t('failedToCreateSession')}: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const createQRSession = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      // Get token with better validation
      const token = session?.access_token || userProfile?.token;
      if (!token) {
        console.error('❌ No authentication token available');
        console.error('Session:', session);
        console.error('UserProfile:', userProfile);
        setError(t('noAuthenticationTokenAvailable') || 'No authentication token available. Please log out and log in again.');
        setSubmitting(false);
        return;
      }

      // Validate token is a string
      if (typeof token !== 'string' || token.trim() === '') {
        console.error('❌ Invalid token format');
        setError('Invalid authentication token. Please log out and log in again.');
        setSubmitting(false);
        return;
      }

      // Validate that at least one product is selected
      if (selectedProducts.length === 0) {
        setError(t('pleaseSelectAtLeastOneProductError'));
        return;
      }

      const totalAmount = calculateTotal();
      if (totalAmount <= 0) {
        setError(t('totalAmountMustBeGreaterThanZero'));
        return;
      }

      const sessionData = {
        products: selectedProducts.map(productId => {
          const productData = {
            product_id: productId,
            quantity: productQuantities[productId] || 1
          };
          // Add offer_id if specified for this product
          if (productOffers[productId] && productOffers[productId].trim() !== '') {
            productData.offer_id = productOffers[productId];
          }
          return productData;
        }),
        final_amount: totalAmount,
        payment_method: 'qr_code'
      };

      // Only include device_id if specified
      if (newSession.device_id) {
        sessionData.device_id = newSession.device_id;
      }

      // Only add offer_id if it's not empty
      if (newSession.offer_id && newSession.offer_id.trim() !== '') {
        sessionData.offer_id = newSession.offer_id;
      }

      console.log('Creating QR session with data:', sessionData);
      console.log('🔍 Validation check - Device ID:', sessionData.device_id);
      console.log('🔍 Validation check - Products:', sessionData.products);
      console.log('🔍 Validation check - Amount:', sessionData.final_amount);
      console.log('🔑 Using token:', token.substring(0, 20) + '...' + token.substring(token.length - 10));

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const response = await fetch(`${apiUrl}/api/merchant/sale-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      // Check response status before parsing JSON
      if (!response.ok) {
        // Try to get error message from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `Server error: ${response.status} ${response.statusText}` };
        }
        console.error('❌ QR Session creation failed:', errorData);
        
        // Check for authentication errors
        if (response.status === 401 || response.status === 403) {
          setError('Authentication failed. Please log out and log in again.');
        } else if (errorData.error && errorData.error.includes('user is not defined')) {
          setError('Server authentication error: Please log out and log in again. The server could not identify your user account.');
        } else {
          setError(errorData.error || errorData.message || `Failed to create QR session: ${response.status} ${response.statusText}`);
        }
        setSubmitting(false);
        return;
      }
      
      const data = await response.json();
      console.log('✅ Create QR session response data:', data);

      if (response.ok && data.success) {
        // Log QR hash code for testing
        if (data.data && data.data.qr_code_hash) {
          console.log('🔍 QR CODE HASH FOR TESTING:', data.data.qr_code_hash);
          console.log('📱 QR Payment URL:', data.data.qr_payment_url || 'Not provided');
        }
        setShowCreateQRSession(false);
        resetForm();
        
        // If QR session was created, show QR code data
        if (data.data.qr_code_hash) {
          // Create custom QR payment URL for mobile app
          const customQRData = {
            type: "payment",
            hash: data.data.qr_code_hash,
            amount: data.data.final_amount,
            merchant_id: "merchant-uuid", // You might need to get this from user profile
            session_id: data.data.id
          };
          
          const qrCodeData = JSON.stringify(customQRData);
          console.log('📱 Custom QR Code Data for Mobile App:', qrCodeData);
          setQrPaymentUrl(qrCodeData);
        }
        
        // Add the newly created QR session locally to active sessions
        if (data.data) {
          const formatted = formatSessionData(data.data);
          setActiveSessions(prev => [formatted, ...prev]);
        }
      } else {
        console.error('❌ QR Session creation failed:', data);
        console.error('📋 Full error response:', JSON.stringify(data, null, 2));
        
        // Show detailed validation errors
        let errorMessage = data.error || data.message || t('failedToCreateSession');
        if (data.validation_errors) {
          errorMessage += `\n${t('validationFailed')}: ${JSON.stringify(data.validation_errors)}`;
        }
        if (data.errors) {
          errorMessage += `\n${t('error')}: ${JSON.stringify(data.errors)}`;
        }
        
        setError(`${t('validationFailed')}: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error creating QR session:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      if (error.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error. Please check if the server is running.');
      } else {
        // Check if error message indicates backend issue
        const errorMessage = error.message || 'Unknown error';
        if (errorMessage.includes('user is not defined')) {
          setError(t('authenticationError') || 'Authentication error: Please log out and log in again. The server could not identify your user account.');
        } else {
          setError(`Failed to create QR session: ${errorMessage}`);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancelSession = async (sessionId) => {
    // Confirm cancellation
    if (!window.confirm(t('areYouSureCancelSession') || 'Are you sure you want to cancel this session?')) {
      return;
    }

    try {
      // Get token with better validation
      const token = session?.access_token || userProfile?.token;
      if (!token) {
        console.error('❌ No authentication token available');
        setError(t('noAuthenticationTokenAvailable') || 'No authentication token available. Please log out and log in again.');
        return;
      }

      // Validate token is a string
      if (typeof token !== 'string' || token.trim() === '') {
        console.error('❌ Invalid token format');
        setError('Invalid authentication token. Please log out and log in again.');
        return;
      }

      console.log('🗑️ Cancelling session:', sessionId);
      console.log('🔑 Using token:', token.substring(0, 20) + '...' + token.substring(token.length - 10));

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const response = await fetch(`${apiUrl}/api/merchant/sale-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Delete session response status:', response.status);
      
      // If status is 200 or 204, consider it success and remove session immediately
      if (response.ok && (response.status === 200 || response.status === 204)) {
        // Parse response first to check if success is true
        let responseData = null;
        let isActuallySuccess = true;
        
        try {
          const responseText = await response.text();
          if (responseText && responseText.trim()) {
            responseData = JSON.parse(responseText);
            console.log('📡 Response data:', responseData);
            
            // Check if server explicitly says success: false
            if (responseData.success === false) {
              isActuallySuccess = false;
              console.warn('⚠️ Server returned success: false');
              setError(responseData.error || 'Session deletion failed on server');
            }
          }
        } catch (parseError) {
          // Empty response is fine for DELETE - treat as success
          console.log('📡 Empty or invalid JSON response (expected for DELETE)');
          isActuallySuccess = true;
        }
        
        // Remove from active sessions immediately if it's a real success
        if (isActuallySuccess) {
          // Add to deleted sessions set to prevent it from reappearing
          deletedSessionIdsRef.current.add(sessionId);
          console.log(`📝 Added session ${sessionId} to deleted sessions list`);
          
          setActiveSessions(prev => {
            const beforeCount = prev.length;
            const filtered = prev.filter(s => s.id !== sessionId);
            const afterCount = filtered.length;
            
            if (beforeCount === afterCount) {
              console.warn(`⚠️ Session ${sessionId} was not found in active sessions list`);
            } else {
              console.log(`✅ Session ${sessionId} removed from list. Before: ${beforeCount}, After: ${afterCount}`);
            }
            
            return filtered;
          });
          
          // Clear any previous errors
          setError(null);
          console.log('✅ Session deleted successfully (status 200/204)');
        } else {
          // Server says it failed, don't remove from list
          console.error('❌ Server reported deletion failure');
          return;
        }
        
        return;
      }

      // Handle non-200/204 responses
      let data;
      try {
        const responseText = await response.text();
        console.log('📡 Response body:', responseText);
        
        if (responseText && responseText.trim()) {
          data = JSON.parse(responseText);
        } else {
          data = { error: `Server error: ${response.status} ${response.statusText}` };
        }
      } catch (parseError) {
        console.error('❌ Error parsing response:', parseError);
        data = { error: `Server error: ${response.status} ${response.statusText}` };
      }

      console.error('❌ Delete session failed:', data);
      
      // Check for authentication errors
      if (response.status === 401 || response.status === 403) {
        setError('Authentication failed. Please log out and log in again.');
      } else if (response.status === 404) {
        setError('Session not found. It may have already been deleted or expired.');
        // Remove from local state anyway if not found
        setActiveSessions(prev => prev.filter(s => s.id !== sessionId));
      } else if (data && data.error) {
        setError(data.error || data.message || 'Failed to delete session');
        // If session not found, remove from local state anyway
        if (data.error.includes('not found') || data.error.includes('Session not found')) {
          setActiveSessions(prev => prev.filter(s => s.id !== sessionId));
        }
      } else {
        setError(t('failedToCancelSession') || `Failed to delete session: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      if (error.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error. Please check if the server is running.');
      } else {
        setError(t('failedToCancelSession') || `Failed to delete session: ${error.message}`);
      }
    }
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting_for_nfc': return 'warning';
      case 'payment_completed': return 'success';
      case 'expired': return 'destructive';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'nfc':
        return '📱';
      case 'qr_code':
        return '📱';
      default:
        return '💳';
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return t('expired');
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) <= new Date();
  };

  const fixMerchantRegistration = async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      console.log('Fixing merchant registration...');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/fix-merchant-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Fix merchant response status:', response.status);
      const data = await response.json();
      console.log('Fix merchant response data:', data);

      if (response.ok && data.success) {
        setError(null);
        // Retry fetching sessions
        fetchSessions();
      } else {
        setError(data.error || t('failedToFixMerchantRegistration'));
      }
    } catch (error) {
      console.error('Error fixing merchant registration:', error);
      setError(t('failedToFixMerchantRegistration'));
    }
  };

  const formatSessionData = useCallback((session) => {
    // Find product name if available
    const product = products.find(p => p.id === session.product_id);
    const productName = product ? product.product_name : t('unknownProduct');
    
    return {
      id: session.id,
      amount: session.final_amount || session.amount || 0,
      description: `${productName} (Qty: ${session.quantity || 1})`,
      status: session.status || 'waiting_for_nfc',
      paymentMethod: session.payment_method || session.paymentMethod || 'nfc',
      nfcTagId: session.nfc_tag_id || session.nfcTagId,
      qrCodeHash: session.qr_code_hash || session.qrCodeHash,
      createdAt: session.created_at || session.createdAt,
      expiresAt: session.session_expires_at || session.expiresAt,
      customerInfo: session.customer_info || session.customerInfo,
      deviceId: session.device_id || session.deviceId,
      productId: session.product_id || session.productId,
      quantity: session.quantity || 1,
      finalAmount: session.final_amount || session.finalAmount || session.amount || 0,
      discountApplied: session.discount_amount || session.discountApplied || 0,
      transactionId: session.transaction_id || session.transactionId,
      completedAt: session.payment_completed_at || session.completedAt,
      productName: productName // Add product name for better display
    };
  }, [products, t]);

  // Filter completed transactions to show only last month
  const getLastMonthTransactions = (transactions) => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    return transactions.filter(session => {
      const completedDate = new Date(session.completedAt || session.createdAt);
      return completedDate >= oneMonthAgo;
    }).sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt));
  };


  // Pagination logic
  const filteredCompletedTransactions = getLastMonthTransactions(completedSessions);
  const totalPages = Math.ceil(filteredCompletedTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredCompletedTransactions.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-muted-foreground">{t('loadingPaymentSessions')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${t('paymentSessions')} 💳`}
        description={t('manageActivePaymentSessions')}
        actions={
          <>
            <Button onClick={() => setShowCreateSession(true)}>
              + {t('nfcSession')}
            </Button>
            <Button onClick={() => setShowCreateQRSession(true)}>
              + {t('qrSession')}
            </Button>
          </>
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center justify-between pt-6 text-destructive">
            <span>{error}</span>
            {error.includes(t('merchantNotFound')) && (
              <Button variant="destructive" size="sm" onClick={fixMerchantRegistration}>
                {t('fixRegistration')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {t('activeSaleSessions')} ({activeSessions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeSessions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💳</div>
              <p className="text-muted-foreground">{t('noActiveSaleSessions')}</p>
              <p className="text-sm text-muted-foreground">{t('createNewSessionToStartAcceptingPayments')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeSessions.map(session => (
                <div
                  key={session.id}
                  className={`border rounded-lg p-4 ${isExpired(session.expiresAt) ? 'border-destructive/50 bg-destructive/5' : 'border-border bg-card'}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getPaymentMethodIcon(session.paymentMethod)}</span>
                      <div>
                        <h3 className="font-semibold text-foreground">
                          {t('session')} {session.id}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {session.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground">
                        SAR {(session.amount || session.final_amount || 0).toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {(session.paymentMethod || session.payment_method || 'nfc').toUpperCase()}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <Badge variant={isExpired(session.expiresAt) ? 'destructive' : getStatusColor(session.status)}>
                        {isExpired(session.expiresAt) ? t('expired') : (session.status || 'unknown').replace('_', ' ')}
                      </Badge>
                      <div className="text-sm text-muted-foreground">
                        {(session.paymentMethod || session.payment_method || 'nfc') === 'nfc' ? (
                          <span>{t('nfcTag')}: {session.nfcTagId || session.nfc_tag_id || t('nA')}</span>
                        ) : (
                          <span>{t('qrHash')}: {session.qrCodeHash || session.qr_code_hash || t('nA')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {!isExpired(session.expiresAt) && (
                        <div className="text-sm text-muted-foreground">
                          {t('expiresIn')}: {formatTimeRemaining(session.expiresAt)}
                        </div>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => cancelSession(session.id)}
                        title={t('cancelSession')}
                      >
                        {t('cancelSession')}
                      </Button>
                    </div>
                  </div>

                  {(session.paymentMethod || session.payment_method || 'nfc') === 'qr' && !isExpired(session.expiresAt) && (
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <div className="text-center">
                        <div className="text-6xl mb-2">📱</div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {t('customerShouldScanThisQRCode')}
                        </p>
                        <div className="bg-card p-2 rounded inline-block border">
                          <div className="w-32 h-32 bg-muted flex items-center justify-center text-xs text-muted-foreground">
                            {t('qrCode')}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t('hash')}: {session.qrCodeHash || session.qr_code_hash || t('nA')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>
            {t('recentCompletedTransactions')} ({filteredCompletedTransactions.length})
            <span className="text-sm font-normal text-muted-foreground ml-2">({t('lastMonthOnly')})</span>
          </CardTitle>
          {filteredCompletedTransactions.length > itemsPerPage && (
            <div className="text-sm text-muted-foreground">
              {t('page')} {currentPage} {t('of')} {totalPages}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {filteredCompletedTransactions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✅</div>
              <p className="text-muted-foreground">
                {completedSessions.length === 0
                  ? t('noCompletedTransactionsYet')
                  : t('noCompletedTransactionsInLastMonth')
                }
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {currentTransactions.map(session => (
                  <div key={session.id} className="border border-border rounded-lg p-4 bg-card">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getPaymentMethodIcon(session.paymentMethod || session.payment_method)}</span>
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {t('transaction')} {session.transactionId || session.id}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">{t('product')}:</span> {session.productName || session.description}
                          </p>
                          {session.customerInfo && (
                            <p className="text-sm text-muted-foreground">
                              {t('customer')}: {session.customerInfo.name} • {session.customerInfo.phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-foreground">
                          SAR {(session.finalAmount || session.final_amount || session.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <Badge variant={getStatusColor(session.status)}>
                          {(session.status || 'unknown').replace('_', ' ')}
                        </Badge>
                        <div className="text-sm text-muted-foreground">
                          {(session.paymentMethod || session.payment_method || 'nfc').toUpperCase()}
                        </div>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        {t('completed')}: {new Date(session.completedAt || session.payment_completed_at || session.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <>
                  <Separator className="my-6" />
                  <div className="flex justify-center items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      ← {t('previous')}
                    </Button>

                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        if (pageNum > totalPages) return null;

                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('next')} →
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showCreateSession}
        onOpenChange={(open) => {
          setShowCreateSession(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('createNewSaleSession')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={createSaleSession} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('deviceOptional')}</Label>
              <Select
                value={newSession.device_id || undefined}
                onValueChange={(v) => setNewSession({...newSession, device_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('autoSelectDefaultDevice')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('autoSelectDefaultDevice')}</SelectItem>
                  {devices.length === 0 && (
                    <SelectItem value="no-devices" disabled>{t('noDevicesAvailable')}</SelectItem>
                  )}
                  {devices.map(device => (
                    <SelectItem key={device.id} value={String(device.id)}>
                      {device.device_name || device.id}
                      {device.merchant_branches && ` (${device.merchant_branches.branch_name})`}
                      {device.is_default && ` - ${t('default')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('ifNotSpecifiedWillUseBranchDefaultDevice')}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t('selectProducts')}</Label>
              <div className="max-h-80 overflow-y-auto border border-border rounded-lg p-4">
                {loadingProducts ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    {t('loadingProducts')}
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="text-4xl mb-2">📦</div>
                    {t('noProductsAvailable')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {products.map(product => (
                      <div
                        key={product.id}
                        className={`relative p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                          selectedProducts.includes(product.id)
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-border bg-card hover:border-muted-foreground/30'
                        }`}
                        onClick={() => handleProductSelection(product.id, !selectedProducts.includes(product.id))}
                      >
                        <div className="absolute top-2 right-2">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedProducts.includes(product.id)
                              ? 'bg-primary border-primary'
                              : 'border-border'
                          }`}>
                            {selectedProducts.includes(product.id) && (
                              <svg className="w-4 h-4 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>

                        <div className="pr-8">
                          <h3 className="font-semibold text-foreground text-sm mb-1 line-clamp-2">
                            {product.product_name}
                          </h3>
                          <p className="text-lg font-bold text-primary">
                            SAR {product.original_price}
                          </p>
                        </div>

                        {selectedProducts.includes(product.id) && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">{t('quantity')}:</Label>
                              <div className="flex items-center space-x-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentQty = productQuantities[product.id] || 1;
                                    if (currentQty > 1) {
                                      handleQuantityChange(product.id, currentQty - 1);
                                    }
                                  }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </Button>
                                <span className="w-8 text-center font-semibold text-foreground">
                                  {productQuantities[product.id] || 1}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentQty = productQuantities[product.id] || 1;
                                    handleQuantityChange(product.id, currentQty + 1);
                                  }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedProducts.length === 0 && (
                <p className="text-sm text-destructive mt-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {t('pleaseSelectAtLeastOneProduct')}
                </p>
              )}
            </div>

            {selectedProducts.length > 0 && (
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-sm font-medium text-foreground mb-2">{t('selectedProducts')}:</h3>
                <div className="space-y-2">
                  {selectedProducts.map(productId => {
                    const product = products.find(p => p.id === productId);
                    const quantity = productQuantities[productId] || 1;
                    const subtotal = product ? product.original_price * quantity : 0;
                    return (
                      <div key={productId} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {product?.product_name} x{quantity}
                        </span>
                        <span className="text-foreground font-medium">
                          SAR {subtotal.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('totalAmountSAR')}</Label>
              <Input
                type="text"
                value={`SAR ${calculateTotal().toFixed(2)}`}
                readOnly
                className="bg-muted font-semibold"
              />
              <p className="text-xs text-muted-foreground">
                {t('totalAutomaticallyCalculated')}
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateSession(false);
                  resetForm();
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting || selectedProducts.length === 0}
              >
                {submitting ? t('creating') : t('createSession')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateQRSession}
        onOpenChange={(open) => {
          setShowCreateQRSession(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('createNewQRSaleSession')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={createQRSession} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('deviceOptional')}</Label>
              <Select
                value={newSession.device_id || undefined}
                onValueChange={(v) => setNewSession({...newSession, device_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('autoSelectDefaultDevice')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('autoSelectDefaultDevice')}</SelectItem>
                  {devices.length === 0 && (
                    <SelectItem value="no-devices" disabled>{t('noDevicesAvailable')}</SelectItem>
                  )}
                  {devices.map(device => (
                    <SelectItem key={device.id} value={String(device.id)}>
                      {device.device_name || device.id}
                      {device.merchant_branches && ` (${device.merchant_branches.branch_name})`}
                      {device.is_default && ` - ${t('default')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('ifNotSpecifiedWillUseBranchDefaultDevice')}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('selectProducts')}</Label>
              <div className="max-h-80 overflow-y-auto border border-border rounded-lg p-4">
                {loadingProducts ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                    Loading products...
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="text-4xl mb-2">📦</div>
                    No products available
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {products.map(product => (
                      <div
                        key={product.id}
                        className={`relative p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                          selectedProducts.includes(product.id)
                            ? 'border-emerald-500 bg-emerald-500/5 shadow-md'
                            : 'border-border bg-card hover:border-muted-foreground/30'
                        }`}
                        onClick={() => handleProductSelection(product.id, !selectedProducts.includes(product.id))}
                      >
                        <div className="absolute top-2 right-2">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedProducts.includes(product.id)
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'border-border'
                          }`}>
                            {selectedProducts.includes(product.id) && (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>

                        <div className="pr-8">
                          <h3 className="font-semibold text-foreground text-sm mb-1 line-clamp-2">
                            {product.product_name}
                          </h3>
                          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                            SAR {product.original_price}
                          </p>
                        </div>

                        {selectedProducts.includes(product.id) && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">{t('quantity')}:</Label>
                              <div className="flex items-center space-x-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentQty = productQuantities[product.id] || 1;
                                    if (currentQty > 1) {
                                      handleQuantityChange(product.id, currentQty - 1);
                                    }
                                  }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </Button>
                                <span className="w-8 text-center font-semibold text-foreground">
                                  {productQuantities[product.id] || 1}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentQty = productQuantities[product.id] || 1;
                                    handleQuantityChange(product.id, currentQty + 1);
                                  }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedProducts.length === 0 && (
                <p className="text-sm text-destructive mt-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {t('pleaseSelectAtLeastOneProduct')}
                </p>
              )}
            </div>

            {selectedProducts.length > 0 && (
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-sm font-medium text-foreground mb-2">{t('selectedProducts')}:</h3>
                <div className="space-y-2">
                  {selectedProducts.map(productId => {
                    const product = products.find(p => p.id === productId);
                    const quantity = productQuantities[productId] || 1;
                    const subtotal = product ? product.original_price * quantity : 0;
                    return (
                      <div key={productId} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {product?.product_name} x{quantity}
                        </span>
                        <span className="text-foreground font-medium">
                          SAR {subtotal.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('totalAmountSAR')}</Label>
              <Input
                type="text"
                value={`SAR ${calculateTotal().toFixed(2)}`}
                readOnly
                className="bg-muted font-semibold"
              />
              <p className="text-xs text-muted-foreground">
                {t('totalAutomaticallyCalculated')}
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateQRSession(false);
                  resetForm();
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting || selectedProducts.length === 0}
              >
                {submitting ? t('creating') : t('createSession')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {notifications.map(notification => (
        <PaymentNotification
          key={notification.id}
          session={notification.session}
          onClose={() => removeNotification(notification.id)}
        />
      ))}

      <Dialog
        open={!!qrPaymentUrl}
        onOpenChange={(open) => {
          if (!open) setQrPaymentUrl(null);
        }}
      >
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle>{t('scanToPay')}</DialogTitle>
          </DialogHeader>
          {qrPaymentUrl && (
            <>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrPaymentUrl)}`}
                alt={t('qrCode')}
                className="mx-auto w-[200px] h-[200px] mb-4"
              />
              <DialogDescription className="break-all text-xs">
                {t('qrData')}: {qrPaymentUrl}
              </DialogDescription>
            </>
          )}
          <p className="text-[10px] text-muted-foreground">
            {t('mobileAppShouldScanQRCode')}
          </p>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => setQrPaymentUrl(null)}>{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentSessions; 